<template>
  <div
    class="modal fade show"
    style="display: block"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-map-marked-alt me-2"></i>
            {{ trip.place }}
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="$emit('close')"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Trip Media Carousel (Images + Videos) -->
          <div v-if="hasMedia" class="mb-4">
            <h6>Media</h6>
            <div class="media-carousel">
              <div class="carousel-container position-relative">
                <!-- Main Media Display -->
                <div
                  class="main-media-container"
                  @mouseenter="onMediaHover"
                  @mouseleave="onMediaLeave"
                >
                  <!-- Image Display -->
                  <img
                    v-if="isCurrentMediaImage"
                    :src="currentMedia.url"
                    :alt="`${trip.place} - ${currentMedia.type} ${
                      currentMediaIndex + 1
                    }`"
                    class="img-fluid rounded shadow-sm main-media"
                  />

                  <!-- Video Display -->
                  <video
                    v-else-if="isCurrentMediaVideo"
                    :src="currentMedia.url"
                    :alt="`${trip.place} - ${currentMedia.type} ${
                      currentMediaIndex + 1
                    }`"
                    class="img-fluid rounded shadow-sm main-media"
                    controls
                    @play="onVideoPlay"
                    @pause="onVideoPause"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>

                <!-- Navigation Arrows -->
                <button
                  v-if="hasMultipleMedia"
                  @click="previousMedia"
                  class="carousel-nav-btn carousel-prev"
                  :disabled="currentMediaIndex === 0"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>

                <button
                  v-if="hasMultipleMedia"
                  @click="nextMedia"
                  class="carousel-nav-btn carousel-next"
                  :disabled="currentMediaIndex === totalMediaCount - 1"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>

                <!-- Media Counter -->
                <div v-if="hasMultipleMedia" class="media-counter">
                  {{ currentMediaIndex + 1 }} / {{ totalMediaCount }}
                </div>

                <!-- Play/Pause Button (for images only) -->
                <button
                  v-if="hasMultipleMedia && isCurrentMediaImage"
                  @click="toggleAutoPlay"
                  class="carousel-control-btn carousel-play-pause"
                  :title="
                    autoPlayInterval ? 'Pause slideshow' : 'Play slideshow'
                  "
                >
                  <i
                    :class="autoPlayInterval ? 'fas fa-pause' : 'fas fa-play'"
                  ></i>
                </button>
              </div>

              <!-- Thumbnail Navigation -->
              <div v-if="hasMultipleMedia" class="thumbnail-nav">
                <div class="thumbnail-container">
                  <div
                    v-for="(media, index) in allMedia"
                    :key="index"
                    @click="goToMedia(index)"
                    class="thumbnail-item"
                    :class="{ active: index === currentMediaIndex }"
                  >
                    <!-- Image Thumbnail -->
                    <img
                      v-if="media.type === 'image'"
                      :src="media.url"
                      :alt="`${trip.place} - Thumbnail ${index + 1}`"
                      class="img-fluid thumbnail-image"
                    />

                    <!-- Video Thumbnail -->
                    <div
                      v-else-if="media.type === 'video'"
                      class="video-thumbnail"
                    >
                      <i class="fas fa-play-circle fa-2x text-white"></i>
                      <span class="video-duration">Video</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Trip Details -->
          <div class="row mb-4">
            <div class="col-md-6">
              <h6>Trip Information</h6>
              <table class="table table-sm">
                <tbody>
                  <tr>
                    <td><strong>Destination:</strong></td>
                    <td>{{ trip.place }}</td>
                  </tr>
                  <tr>
                    <td><strong>Date:</strong></td>
                    <td>{{ formatDate(trip.date) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Duration:</strong></td>
                    <td>{{ trip.days }} days</td>
                  </tr>
                  <tr>
                    <td><strong>Price:</strong></td>
                    <td class="text-primary fw-bold">
                      &#8377;{{ trip.price }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="col-md-6">
              <h6>Additional Information</h6>
              <table class="table table-sm">
                <tbody>
                  <tr>
                    <td><strong>Created:</strong></td>
                    <td>{{ formatDate(trip.createdAt) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Last Updated:</strong></td>
                    <td>{{ formatDate(trip.updatedAt) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>
                      <span class="badge bg-success">Active</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Description -->
          <div class="mb-4">
            <h6>Description</h6>
            <p class="text-muted">{{ trip.description }}</p>
          </div>

          <!-- Links -->
          <div class="mb-4">
            <h6>Resources</h6>
            <div class="d-flex gap-2 flex-wrap">
              <a
                v-if="trip.googleSheetUrl"
                :href="trip.googleSheetUrl"
                target="_blank"
                class="btn btn-outline-success btn-sm"
              >
                <i class="fas fa-table me-2"></i>
                Google Sheet
                <i class="fas fa-external-link-alt ms-1"></i>
              </a>

              <a
                v-if="trip.itineraryPdf"
                :href="trip.itineraryPdf"
                target="_blank"
                class="btn btn-outline-info btn-sm"
              >
                <i class="fas fa-file-pdf me-2"></i>
                Itinerary PDF
                <i class="fas fa-external-link-alt ms-1"></i>
              </a>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="$emit('close')"
          >
            Close
          </button>
          <router-link
            :to="`/trips/${trip.id}/edit`"
            class="btn btn-primary"
            @click="$emit('close')"
          >
            <i class="fas fa-edit me-2"></i>
            Edit Trip
          </router-link>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<script>
import moment from "moment";

export default {
  name: "TripDetailsModal",
  props: {
    trip: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  data() {
    return {
      currentMediaIndex: 0,
      autoPlayInterval: null,
      autoPlayDelay: 3000, // 3 seconds
    };
  },
  mounted() {
    // Start auto-play if there are multiple media items and current is image
    this.startAutoPlay();

    // Add keyboard navigation
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    // Clean up auto-play and event listeners
    this.stopAutoPlay();
    document.removeEventListener("keydown", this.handleKeydown);
  },
  computed: {
    hasMedia() {
      return this.trip.images?.length > 0 || this.trip.video;
    },
    hasMultipleMedia() {
      return this.totalMediaCount > 1;
    },
    totalMediaCount() {
      return this.allMedia.length;
    },
    allMedia() {
      const media = [];

      // Add images
      if (this.trip.images) {
        this.trip.images.forEach((url, index) => {
          media.push({
            type: "image",
            url: url,
            index: index,
          });
        });
      }

      // Add video
      if (this.trip.video) {
        media.push({
          type: "video",
          url: this.trip.video,
          index: media.length,
        });
      }

      return media;
    },
    currentMedia() {
      return this.allMedia[this.currentMediaIndex] || null;
    },
    isCurrentMediaImage() {
      return this.currentMedia?.type === "image";
    },
    isCurrentMediaVideo() {
      return this.currentMedia?.type === "video";
    },
  },
  methods: {
    formatDate(dateString) {
      return moment(dateString).format("MMMM DD, YYYY");
    },
    nextMedia() {
      if (this.hasMultipleMedia) {
        this.currentMediaIndex =
          (this.currentMediaIndex + 1) % this.totalMediaCount;
      }
    },
    previousMedia() {
      if (this.hasMultipleMedia) {
        this.currentMediaIndex =
          (this.currentMediaIndex - 1 + this.totalMediaCount) %
          this.totalMediaCount;
      }
    },
    goToMedia(index) {
      if (this.hasMultipleMedia && index >= 0 && index < this.totalMediaCount) {
        this.currentMediaIndex = index;
      }
    },
    startAutoPlay() {
      if (this.hasMultipleMedia && this.isCurrentMediaImage) {
        this.autoPlayInterval = setInterval(() => {
          // Only auto-advance if current media is an image
          if (this.isCurrentMediaImage) {
            this.nextMedia();
          }
        }, this.autoPlayDelay);
      }
    },
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    },
    toggleAutoPlay() {
      if (this.autoPlayInterval) {
        this.stopAutoPlay();
      } else {
        this.startAutoPlay();
      }
    },
    handleKeydown(event) {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          this.previousMedia();
          break;
        case "ArrowRight":
          event.preventDefault();
          this.nextMedia();
          break;
        case " ":
          event.preventDefault();
          if (this.isCurrentMediaImage) {
            if (this.autoPlayInterval) {
              this.stopAutoPlay();
            } else {
              this.startAutoPlay();
            }
          }
          break;
      }
    },
    onMediaHover() {
      // Pause auto-play when hovering over media (only for images)
      if (this.isCurrentMediaImage) {
        this.stopAutoPlay();
      }
    },
    onMediaLeave() {
      // Resume auto-play when leaving media (only for images)
      if (this.isCurrentMediaImage) {
        this.startAutoPlay();
      }
    },
    onVideoPlay() {
      // Stop auto-play when video starts playing
      this.stopAutoPlay();
    },
    onVideoPause() {
      // Resume auto-play when video is paused (if it's an image)
      if (this.isCurrentMediaImage) {
        this.startAutoPlay();
      }
    },
  },
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

.table td {
  border: none;
  padding: 0.5rem 0;
}

.table td:first-child {
  width: 40%;
}

.media-carousel {
  position: relative;
  width: 100%;
  height: 400px; /* Adjust height as needed */
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.main-media-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.main-media {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.5s ease-in-out;
  opacity: 1;
}

.main-media.fade-enter-active,
.main-media.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.main-media.fade-enter-from,
.main-media.fade-leave-to {
  opacity: 0;
}

.carousel-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s ease;
}

.carousel-nav-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.carousel-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.carousel-prev {
  left: 10px;
}

.carousel-next {
  right: 10px;
}

.media-counter {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.9rem;
  z-index: 10;
}

.thumbnail-nav {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.thumbnail-container {
  display: flex;
  gap: 5px;
  padding: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
}

.thumbnail-item {
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.3s ease;
}

.thumbnail-item.active {
  border-color: white;
}

.thumbnail-image {
  width: 60px; /* Adjust thumbnail size */
  height: 40px; /* Adjust thumbnail size */
  object-fit: cover;
  border-radius: 4px;
}

.video-thumbnail {
  width: 60px;
  height: 40px;
  background-color: #333;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.video-thumbnail .fa-play-circle {
  color: white;
  margin-bottom: 2px;
}

.video-duration {
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
}

.carousel-play-pause {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s ease;
}

.carousel-play-pause:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.carousel-control-btn {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s ease;
}

.carousel-control-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}
</style>
